import {
  Stack,
  Box,
  Button as MuiButton,
  Card,
  Divider,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useBillsQuery } from '../../../api/getBills';

import { usePoliciesQuery } from '../../../api/getPolicies';
import AutoHome from '../../../assets/icons/AutoHome';
import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';
import CrossSellExpandedCard from '../CrossSellExpandedCard';
import QuoteFormRedirect from '../CrossSellExpandedCard/QuoteFormRedirect';
import { TextButton } from '../../styledComponents/Button';
import { clubsWithNoMyQuoteLink } from './constants';
import { getBundledDiscounts } from '../../../utils/nextGenUtils';
import useGetFeatureFlags from '../../../hooks/useGetFeatureFlags';

const TabletAutoHomeCard = () => {
  const [expandedCardOpen, setExpandedCardOpen] = useState<boolean>(false);
  const { data } = usePoliciesQuery();
  const { data: billsData } = useBillsQuery();
  const { enableNextGen } = useGetFeatureFlags();

  const crossSellInfo = data?.crossSellInfo;
  const { track } = useAnalytics();

  const clubCode = crossSellInfo?.clubInfo?.clubCode;
  const hasClubLink = !clubsWithNoMyQuoteLink.includes(clubCode as string);

  if (!crossSellInfo?.crossSellEligible || !billsData) return null;

  // NEXTGEN-FLAG: on flag retirement, stop passing the flag (see getBundledDiscounts)
  const { autoDiscount, propertyDiscount } = getBundledDiscounts(
    data,
    enableNextGen
  );

  const handleExpandedCardClose = () => {
    setExpandedCardOpen(false);
  };

  return (
    <Card
      data-testid="tabletAutoHomeCard"
      component={Stack}
      spacing={4}
      direction="row"
      sx={{
        boxShadow: '0px 4px 8px rgba(25, 30, 43, 0.08)',
        p: 3,
        borderRadius: 2,
        height: 'auto',
      }}
    >
      <Stack spacing={3} direction="row" sx={{ width: 1 }}>
        <Box
          sx={{
            backgroundColor: colors.csaaGray,
            borderRadius: 1,
            textAlign: 'center',
            py: 3,
            px: 6,
          }}
        >
          <AutoHome sx={{ width: 60, height: 60 }} />
        </Box>
        <Stack
          sx={{
            border: 'none',
            borderColor: colors.csaaBorder,
            borderRadius: 2,
            flex: 2,
            justifyContent: 'flex-end',
          }}
          spacing={1}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 18,
              lineHeight: '21px',
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaBlack,
            }}
          >
            Bundle and save up to:
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              fontSize: 18,
              lineHeight: '20px',
              fontWeight: 400,
              color: colors.csaaBlack,
            }}
          >
            {autoDiscount}% on your auto insurance*
          </Typography>
          <Divider sx={{ my: 2, display: 'none' }} />
          <Typography
            sx={{
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              fontSize: 18,
              lineHeight: '20px',
              fontWeight: 400,
              color: colors.csaaBlack,
            }}
          >
            {propertyDiscount}% with AAA Home insurance*
          </Typography>
          <MuiButton
            sx={{
              justifyContent: 'flex-start',
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 700,
              lineHeight: '21px',
              p: 0,
            }}
            onClick={() => {
              track('Promotion Clicked', {
                event_type: 'UX Modified',
                contents: 'Bundle and save up to:',
                link: 'Show more',
                location: 'Secondary Card',
              });
              setExpandedCardOpen(true);
            }}
          >
            Show more
          </MuiButton>
        </Stack>
        {hasClubLink && (
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <QuoteFormRedirect>
              <TextButton
                sx={{ p: 1 }}
                type="submit"
                onClick={() => {
                  track('Promotion Clicked', {
                    event_type: 'Link Accessed',
                    contents: 'Bundle and save up to:',
                    link: 'Get a quote',
                    location: 'Secondary Card',
                  });
                }}
              >
                Get a quote
              </TextButton>
            </QuoteFormRedirect>
          </Stack>
        )}
      </Stack>
      <CrossSellExpandedCard
        hasClubLink={hasClubLink}
        open={expandedCardOpen}
        autoDiscount={autoDiscount}
        propertyDiscount={propertyDiscount}
        closeDialog={handleExpandedCardClose}
      />
    </Card>
  );
};

export default TabletAutoHomeCard;
